import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { message } from 'antd';
import {
  ClipboardDocumentIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  EmptyState,
  PageHeader,
  StatusBadge,
  TWButton,
  TWConfirmModal,
  TWDataTable,
} from '../../components/ui';
import { DataTableSkeleton, StackedText, colTitle } from '../../data/columns';
import fleetApi from '../../services/fleetApi';
import AdminOnly from './components/admin-only';
import DevicesNav from './components/devices-nav';
import ReleaseUploadModal from './components/release-upload-modal';
import useFleetPoll, { fleetErrorText } from './use-fleet-poll';
import { APPS, formatBytes } from './fleet-utils';

const APP_TONES = { kiosk: 'blue', kitchen: 'violet', agent: 'slate' };
const COPIED_MS = 2000;
const DASH = '—';

const SKELETON_HEADERS = [
  { label: 'App', shape: 'pill', w: 'w-16' },
  { label: 'Version', w: 'w-28' },
  { label: 'Size', align: 'right', w: 'w-16' },
  { label: 'SHA-256', w: 'w-28' },
  { label: 'Uploaded', w: 'w-36' },
  { label: 'Current', shape: 'pill', w: 'w-16' },
  { label: 'Devices', align: 'right', w: 'w-8' },
  { label: '', align: 'right', w: 'w-32' },
];

const appLabel = (app) => APPS.find((a) => a.value === app)?.label || app;

const Releases = () => (
  <AdminOnly>
    <ReleasesList />
  </AdminOnly>
);

const ReleasesList = () => {
  const { data, loading, fetching, error, reload } = useFleetPoll(
    () => fleetApi.listReleases().then((res) => res.data.data),
    {
      intervalMs: 0,
      onRefetchError: (err) => message.error(fleetErrorText(err, 'Could not load releases')),
    },
  );
  const rows = data || [];

  const [uploadOpen, setUploadOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind: 'current' | 'delete', release }
  const [acting, setActing] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const copiedTimer = useRef(null);
  // The last confirm stays readable while the modal fades out.
  const lastConfirm = useRef(null);
  if (confirm) lastConfirm.current = confirm;

  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const copySha = async (release) => {
    const clipboard = navigator.clipboard;
    if (!clipboard?.writeText) return;
    try {
      await clipboard.writeText(release.sha256);
    } catch (err) {
      return;
    }
    setCopiedId(release.id);
    clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopiedId(null), COPIED_MS);
  };

  const closeConfirm = () => {
    if (!acting) setConfirm(null);
  };

  const runConfirmed = async () => {
    if (!confirm || acting) return;
    const { kind, release } = confirm;
    setActing(true);
    try {
      if (kind === 'current') {
        await fleetApi.markCurrent(release.id);
        message.success('Marked current');
      } else {
        await fleetApi.deleteRelease(release.id);
        message.success('Release deleted');
      }
      reload();
    } catch (err) {
      message.error(
        fleetErrorText(
          err,
          kind === 'current' ? 'Could not mark the release current' : 'Could not delete the release',
        ),
      );
    } finally {
      setActing(false);
      setConfirm(null);
    }
  };

  const columns = [
    {
      title: colTitle('App'),
      key: 'app',
      render: (_, r) => (
        <StatusBadge size="sm" tone={APP_TONES[r.app] || 'slate'} label={appLabel(r.app)} />
      ),
    },
    {
      title: colTitle('Version'),
      key: 'version',
      render: (_, r) => (
        <StackedText primary={r.versionname} secondary={`code ${r.versioncode}`} />
      ),
    },
    {
      title: colTitle('Size', { align: 'right' }),
      key: 'size',
      align: 'right',
      render: (_, r) => <span className="tabular-nums">{formatBytes(r.sizebytes)}</span>,
    },
    {
      title: colTitle('SHA-256'),
      key: 'sha256',
      render: (_, r) => (
        <div className="flex items-center gap-1">
          <span className="font-mono text-xs text-slate-600 dark:text-slate-300">
            {(r.sha256 || '').slice(0, 12)}
          </span>
          <TWButton
            variant="ghost"
            size="sm"
            icon={ClipboardDocumentIcon}
            aria-label={copiedId === r.id ? 'Copied' : 'Copy SHA-256'}
            title="Copy SHA-256"
            onClick={() => copySha(r)}
          >
            {copiedId === r.id ? 'Copied' : null}
          </TWButton>
        </div>
      ),
    },
    {
      title: colTitle('Uploaded'),
      key: 'uploaded',
      render: (_, r) => (
        <StackedText
          primary={r.uploadedbyemail || DASH}
          secondary={r.createdate ? dayjs(r.createdate).format('MMM D, YYYY HH:mm') : DASH}
        />
      ),
    },
    {
      title: colTitle('Current'),
      key: 'current',
      render: (_, r) =>
        r.iscurrent ? <StatusBadge size="sm" tone="emerald" label="Current" /> : null,
    },
    {
      title: colTitle('Devices', { align: 'right' }),
      key: 'devices',
      align: 'right',
      render: (_, r) => <span className="tabular-nums">{r.devicecount ?? 0}</span>,
    },
    {
      title: <span className="sr-only">Actions</span>,
      key: 'actions',
      align: 'right',
      render: (_, r) => (
        <div className="flex items-center justify-end gap-1">
          {r.iscurrent ? null : (
            <TWButton
              variant="ghost"
              size="sm"
              onClick={() => setConfirm({ kind: 'current', release: r })}
            >
              Mark current
            </TWButton>
          )}
          {r.iscurrent ? null : (
            <TWButton
              variant="ghost"
              size="sm"
              className="text-rose-600 hover:text-rose-700 dark:text-rose-400"
              onClick={() => setConfirm({ kind: 'delete', release: r })}
            >
              Delete
            </TWButton>
          )}
        </div>
      ),
    },
  ];

  const openUpload = () => setUploadOpen(true);

  let body;
  if (error) {
    body = (
      <EmptyState
        icon={ExclamationTriangleIcon}
        title="Could not load releases"
        description={fleetErrorText(error)}
        action={<TWButton onClick={reload}>Retry</TWButton>}
      />
    );
  } else if (loading) {
    body = <DataTableSkeleton headers={SKELETON_HEADERS} rows={6} />;
  } else if (rows.length === 0) {
    body = (
      <EmptyState
        icon={CloudArrowUpIcon}
        title="No releases yet"
        description="Upload an APK to make it available for rollouts."
        action={
          <TWButton variant="primary" onClick={openUpload}>
            Upload release
          </TWButton>
        }
      />
    );
  } else {
    body = (
      <div className="reveal">
        <TWDataTable columns={columns} data={rows} loading={fetching} />
      </div>
    );
  }

  const shown = confirm || lastConfirm.current;
  const release = shown?.release;
  const isCurrentConfirm = shown?.kind === 'current';

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <PageHeader
          title="Releases"
          description="APK builds the fleet can install."
          actions={
            <TWButton variant="primary" onClick={openUpload}>
              Upload release
            </TWButton>
          }
        />
        <DevicesNav current="releases" />
        {body}
      </div>

      <ReleaseUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={reload}
      />

      <TWConfirmModal
        open={Boolean(confirm)}
        onClose={closeConfirm}
        onConfirm={runConfirmed}
        loading={acting}
        tone={isCurrentConfirm ? 'warning' : 'danger'}
        title={
          isCurrentConfirm
            ? `Mark ${release?.versionname} current?`
            : `Delete ${release?.versionname}?`
        }
        description={
          isCurrentConfirm
            ? 'A later rollout of this release will have no automatic rollback target. Safer: roll it out and let the rollout mark it current.'
            : 'The APK is removed from storage. This cannot be undone.'
        }
        confirmLabel={isCurrentConfirm ? 'Mark current' : 'Delete'}
      />
    </div>
  );
};

export default Releases;
